import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../../redux/store";
import { Share2 } from "lucide-react";
import { fetchPosts } from "../../../../redux/blogSlice";

const EArticle: React.FC = () => {
  const { slug } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { posts } = useSelector((state: RootState) => state.blog);
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [modalIndex, setModalIndex] = useState<number>(0);
  const imageUrls = useRef<string[]>([]);

  const post = posts.find((p) => p.slug === slug);

  useEffect(() => {
    if (!posts.length) dispatch(fetchPosts());
  }, [dispatch, posts.length]);

  useEffect(() => {
    if (!post) return;
    const container = document.getElementById("article-content");
    if (!container) return;

    const imgs = container.querySelectorAll("img");
    imageUrls.current = [];

    imgs.forEach((img, index) => {
      const el = img as HTMLImageElement;
      el.style.width = "100%";
      el.style.height = "200px";
      el.style.objectFit = "cover";
      el.style.borderRadius = "8px";
      el.style.cursor = "pointer";
      el.style.flex = "1 1 calc(50% - 8px)";
      el.onclick = () => {
        setModalImage(el.src);
        setModalIndex(index);
      };
      imageUrls.current.push(el.src);
    });

    if (imgs.length > 1) {
      container.style.display = "flex";
      container.style.flexWrap = "wrap";
      container.style.gap = "16px";
    }
  }, [post]);

  const closeModal = () => setModalImage(null);

  const showPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (modalIndex > 0) {
      const newIndex = modalIndex - 1;
      setModalIndex(newIndex);
      setModalImage(imageUrls.current[newIndex]);
    }
  };

  const showNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (modalIndex < imageUrls.current.length - 1) {
      const newIndex = modalIndex + 1;
      setModalIndex(newIndex);
      setModalImage(imageUrls.current[newIndex]);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard");
    }
  };

  if (!post) return <p style={{ color: "#fff", padding: 24 }}>Loading...</p>;

  const createdAt = post.createdAt
    ? new Date(
        typeof post.createdAt === "string"
          ? post.createdAt
          : post.createdAt.seconds * 1000
      )
    : null;

  const year = new Date().getFullYear();

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        overflowX: "hidden",
        background: "linear-gradient(160deg, #0a0014, #1e003a, #bf9b30, #ffdc73, #111)",
        paddingBottom: 80,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          maxWidth: 800,
          width: "100%",
          color: "#fff",
          padding: "2rem 1rem",
          position: "relative",
        }}
      >
        <div
          onClick={handleShare}
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            cursor: "pointer",
            zIndex: 1,
          }}
        >
          <Share2 size={20} color="#ffdc73" />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span
            style={{
              padding: "4px 12px",
              border: "1px solid #ffdc73",
              borderRadius: 20,
              color: "#ffdc73",
              fontSize: 12,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {post.category || "Uncategorized"}
          </span>
        </div>

        <h1 style={{ fontSize: 32, fontWeight: "bold", marginBottom: 16 }}>{post.title}</h1>

        {post.featuredImage && (
          <img
            src={post.featuredImage}
            alt="cover"
            style={{
              width: "100%",
              maxHeight: 360,
              objectFit: "cover",
              borderRadius: 12,
              marginBottom: 16,
              display: "block",
            }}
          />
        )}

        <p style={{ fontSize: 16, fontStyle: "italic", marginBottom: 8, color: "#ccc" }}>
          {post.description}
        </p>

        {createdAt && (
          <p style={{ fontSize: 13, fontStyle: "italic", color: "#aaa", marginBottom: 24 }}>
            {createdAt.toLocaleString("en-GB", {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              timeZoneName: "short",
            })}
          </p>
        )}

        <div
          id="article-content"
          style={{
            fontSize: 16,
            lineHeight: 1.8,
            overflowWrap: "break-word",
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </div>

      {/* Modal */}
      {modalImage && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(8px)",
            padding: 20,
          }}
        >
          {/* Prev */}
          {modalIndex > 0 && (
            <button
              onClick={showPrev}
              style={{
                position: "absolute",
                left: 20,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                color: "#c084fc",
                fontSize: 40,
                border: "none",
                cursor: "pointer",
                zIndex: 99999,
              }}
            >
              ‹
            </button>
          )}

          <img
            src={modalImage}
            alt="preview"
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: 12,
              boxShadow: "0 0 20px rgba(255,255,255,0.2)",
            }}
          />

          {/* Next */}
          {modalIndex < imageUrls.current.length - 1 && (
            <button
              onClick={showNext}
              style={{
                position: "absolute",
                right: 20,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                color: "#c084fc",
                fontSize: 40,
                border: "none",
                cursor: "pointer",
                zIndex: 99999,
              }}
            >
              ›
            </button>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "#0a0014",
          color: "#aaa",
          textAlign: "center",
          padding: "12px 0",
          fontSize: 13,
          borderTop: "1px solid #333",
        }}
      >
        © 4REVA GROUP {year}
      </div>
    </div>
  );
};

export default EArticle;


const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: "100vh",
    width: "100vw",
    background: "linear-gradient(160deg, #0a0014, #1e003a, #bf9b30, #ffdc73, #111)",
    padding: "2rem 1rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  container: {
    maxWidth: 800,
    width: "100%",
    color: "#fff",
    fontFamily: "'Segoe UI', sans-serif",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  category: {
    padding: "4px 12px",
    border: "1px solid #ff69b4",
    borderRadius: 20,
    color: "#ff69b4",
    fontSize: 12,
    textTransform: "uppercase",
    fontWeight: 600,
  },
  date: {
    fontSize: 13,
    color: "#000",
    fontStyle: "italic",
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 16,
    lineHeight: 1.3,
  },
  image: {
    width: "100%",
    maxHeight: 360,
    objectFit: "cover",
    borderRadius: 12,
    marginBottom: 16,
  },
  description: {
    fontSize: 16,
    fontStyle: "italic",
    marginBottom: 24,
    color: "#ccc",
  },
  content: {
    fontSize: 16,
    lineHeight: 1.8,
  },
};
